import React, { useState } from "react";
import { Col, Row, Select, Space, theme, Typography } from "antd";
import { dayjs } from "../../Utilities/dayjs";
import { Dayjs } from "dayjs";
import SubmissionCard from "./SubmissionCard";
import PracticeUserCard from "./PracticeUserCard";
import InvoiceCard from "./InvoiceCard";
import UpdateCard from "./UpdateCard";
import HolidayCard from "./HolidayCard";
import HeaderStats from "./HeaderStats";
import CustomRangePicker from "../../Common/Custom Components/Ant design/RangePicker";
import { IPractices, usePractice } from "../../Context/PracticeContext";
import { PageLayout, PageHeader, SectionCard } from "../../Common/Layout";

const { Text } = Typography;

const Dashboard = () => {
  const { token } = theme.useToken();
  const [selectedDate, setSelectedDate] = useState<Dayjs[]>([dayjs().startOf('month'), dayjs().endOf("day")]);
  const [selectPId, setSelectPId] = React.useState<number | null>(null);
  const { allPracticeIds } = usePractice();
  const isLiveURL = window.location.origin === "https://admin.actingoffice.com";

  const removePractice = new Set([6, 10]);
  const filteredPracticeIds = isLiveURL ? allPracticeIds?.filter((p) => !removePractice.has(p.value)) : allPracticeIds;

  React.useEffect(() => {
    if (filteredPracticeIds?.length > 0 && !selectPId) {
      setSelectPId(filteredPracticeIds[0].value);
    }
  }, [filteredPracticeIds, selectPId]);

  const dateRangePicker = (
    <CustomRangePicker
      value={[selectedDate[0], selectedDate[1]]}
      allowClear={false}
      onChange={(dates) => setSelectedDate([dayjs(dates[0]), dayjs(dates[1])])}
    />
  );

  return (
    <PageLayout>
      <PageHeader
        title="Dashboard"
        subtitle="Monitor financial metrics, submissions, invoices, and practice activity"
        metadata={[{ label: "Period", value: `${selectedDate[0].format("DD MMM YYYY")} – ${selectedDate[1].format("DD MMM YYYY")}` }]}
        actions={[dateRangePicker]}
      />

      {/* ── Financial Stats ── */}
      <SectionCard title="Financial Overview">
        <HeaderStats
          fromDate={selectedDate[0].format("DD-MM-YYYY")}
          toDate={selectedDate[1].format("DD-MM-YYYY")}
        />
      </SectionCard>

      {/* ── Submissions & Invoices ── */}
      <SectionCard
        title="Submissions & Invoices"
        extra={
          <Space size={8}>
            <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>Practice:</Text>
            <Select
              value={selectPId}
              onChange={setSelectPId}
              style={{ width: 220 }}
              placeholder="Select Practice"
              showSearch
              size="small"
              filterOption={(input: any, option: any) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              options={filteredPracticeIds.map((p: IPractices) => ({
                value: p.value,
                label: p.label + " (ID: " + p.value + ")",
              }))}
            />
          </Space>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <SubmissionCard
              practiceId={selectPId}
              fromDate={selectedDate[0].format("DD-MM-YYYY")}
              toDate={selectedDate[1].format("DD-MM-YYYY")}
            />
          </Col>
          <Col xs={24} lg={12}>
            <InvoiceCard
              practiceId={selectPId}
              fromDate={selectedDate[0].format("DD-MM-YYYY")}
              toDate={selectedDate[1].format("DD-MM-YYYY")}
            />
          </Col>
        </Row>
      </SectionCard>

      {/* ── Practice Users ── */}
      <SectionCard title="Practice Users">
        <PracticeUserCard
          fromDate={selectedDate[0].format("DD-MM-YYYY")}
          toDate={selectedDate[1].format("DD-MM-YYYY")}
        />
      </SectionCard>

      {/* ── Updates & Holidays ── */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={12} style={{ display: "flex" }}>
          <UpdateCard
            fromDate={selectedDate[0].format("DD-MM-YYYY")}
            toDate={selectedDate[1].format("DD-MM-YYYY")}
          />
        </Col>
        <Col xs={24} lg={12} style={{ display: "flex" }}>
          <HolidayCard
            fromDate={selectedDate[0].format("DD-MM-YYYY")}
            toDate={selectedDate[1].format("DD-MM-YYYY")}
          />
        </Col>
      </Row>

    </PageLayout>
  );
};

export default Dashboard;
